import { useState, useEffect } from "react";
import { Clock, Save, AlertTriangle, Lock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAutoClosePeriod } from "@/hooks/useAutoClosePeriod";

export function AutoClosePeriodCard() {
  const { settings, isLoading, updateSettings } = useAutoClosePeriod();
  
  const [enabled, setEnabled] = useState(settings.enabled);
  const [requireManualClose, setRequireManualClose] = useState(settings.requireManualClose);
  const [hours, setHours] = useState(String(settings.hours));
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setEnabled(settings.enabled);
    setRequireManualClose(settings.requireManualClose);
    setHours(String(settings.hours));
  }, [settings]);

  useEffect(() => {
    const changed = 
      enabled !== settings.enabled || 
      requireManualClose !== settings.requireManualClose ||
      parseInt(hours) !== settings.hours;
    setHasChanges(changed);
  }, [enabled, requireManualClose, hours, settings]);

  const handleSave = async () => {
    setIsSaving(true);
    await updateSettings({
      enabled,
      requireManualClose,
      hours: parseInt(hours),
    });
    setIsSaving(false);
    setHasChanges(false);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Fechamento Automático de Período
        </CardTitle>
        <CardDescription>
          Configura o comportamento quando o período vira a meia-noite e há inatividade
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div>
            <Label className="text-base">Ativar Gerenciamento de Período</Label>
            <p className="text-sm text-muted-foreground">
              Monitora quando o período vira a meia-noite e há inatividade
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
          />
        </div>

        {enabled && (
          <div className="flex items-center justify-between rounded-lg border p-4 bg-amber-50/50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800">
            <div>
              <Label className="text-base flex items-center gap-2">
                <Lock className="h-4 w-4 text-amber-600" />
                Exigir Fechamento Manual
              </Label>
              <p className="text-sm text-muted-foreground">
                Em vez de fechar automaticamente, bloqueia o PDV e exige que o operador feche o período manualmente
              </p>
            </div>
            <Switch
              checked={requireManualClose}
              onCheckedChange={setRequireManualClose}
            />
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="inactivityHours" className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-warning" />
            {requireManualClose ? "Bloquear após inatividade de:" : "Fechar após inatividade de:"}
          </Label>
          <Select 
            value={hours} 
            onValueChange={setHours}
            disabled={!enabled}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Selecione as horas" />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((h) => (
                <SelectItem key={h} value={String(h)}>
                  {h} {h === 1 ? "hora" : "horas"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {requireManualClose 
              ? `Se passar da meia-noite e ficar mais de ${hours} hora(s) sem novos pedidos, o PDV será bloqueado até o operador fechar o período.`
              : `Se passar da meia-noite e ficar mais de ${hours} hora(s) sem novos pedidos, o período será fechado automaticamente e um novo período será aberto para o dia atual.`
            }
          </p>
        </div>

        {hasChanges && (
          <Button onClick={handleSave} disabled={isSaving} className="gap-2">
            <Save className="h-4 w-4" />
            {isSaving ? "Salvando..." : "Salvar Alterações"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
